import { QueryClientProvider } from '@tanstack/react-query';
import { Toast } from '@heroui/react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { queryClient } from '../lib/queryClient';
import { DashboardLayout } from './components/DashboardLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SwipeDismissToast } from './components/SwipeDismissToast';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { TenantProvider } from './contexts/TenantContext';
import { ToastProvider } from './contexts/ToastContext';
import AdminDashboardPage from './pages/AdminDashboardPage';
import BranchesPage from './pages/BranchesPage';
import BusinessIntelligencePage from './pages/BusinessIntelligencePage';
import BusinessOverviewPage from './pages/BusinessOverviewPage';
import BusinessSettingsPage from './pages/BusinessSettingsPage';
import ControlTowerPage from './pages/ControlTowerPage';
import DeliveryPage from './pages/DeliveryPage';
import DeliveryStaffPage from './pages/DeliveryStaffPage';
import AiEscalationsPage from './pages/AiEscalationsPage';
import EmployeeLoginPage from './pages/EmployeeLoginPage';
import KitchenPage from './pages/KitchenPage';
import LoginPage from './pages/LoginPage';
import MenuManagementPage from './pages/MenuManagementPage';
import InventoryPage from './pages/inventory/InventoryPage';
import InventoryAlertsPage from './pages/inventory/AlertsPage';
import InventoryImportPage from './pages/inventory/ImportPage';
import InventoryTemplatesPage from './pages/inventory/TemplatesPage';
import NotFoundPage from './pages/NotFoundPage';
import OrderStatusPage from './pages/OrderStatusPage';
import PublicMenuPage from './pages/PublicMenuPage';
import ReceptionPage from './pages/ReceptionPage';
import UsersPage from './pages/UsersPage';
import WaiterPage from './pages/WaiterPage';
import EstoyPidiendoLanding from './pages/estoypidiendo-landing/EstoyPidiendoLanding';
import { isPlatformLandingHost } from './lib/tenant';

const businessRoles = ['owner', 'manager', 'reception', 'kitchen', 'delivery', 'waiter'] as const;

function RootRoute() {
  return isPlatformLandingHost() ? <EstoyPidiendoLanding /> : <PublicMenuPage />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <Toast.Provider placement="top end">
            {({ toast }) => <SwipeDismissToast toast={toast} />}
          </Toast.Provider>
          <AuthProvider>
            <TenantProvider>
              <CartProvider>
                <Routes>
                  <Route path="/" element={<RootRoute />} />
                  <Route path="/order/:token" element={<OrderStatusPage />} />
                  <Route path="/delivery/:token" element={<DeliveryPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/employee" element={<EmployeeLoginPage />} />
                  <Route
                    path="/admin"
                    element={<ProtectedRoute roles={['super_admin']}><AdminDashboardPage /></ProtectedRoute>}
                  />
                  <Route
                    path="/dashboard"
                    element={<ProtectedRoute roles={[...businessRoles]}><DashboardLayout /></ProtectedRoute>}
                  >
                    <Route index element={<ProtectedRoute roles={['owner', 'manager']}><BusinessOverviewPage /></ProtectedRoute>} />
                    <Route path="intelligence" element={<ProtectedRoute roles={['owner', 'manager']}><BusinessIntelligencePage /></ProtectedRoute>} />
                    <Route path="torre-control" element={<ProtectedRoute roles={['owner', 'manager']}><ControlTowerPage /></ProtectedRoute>} />
                    <Route path="menu" element={<ProtectedRoute roles={['owner', 'manager']}><MenuManagementPage /></ProtectedRoute>} />
                    <Route path="inventario" element={<Navigate replace to="/dashboard/inventario/stock" />} />
                    <Route path="inventario/stock" element={<ProtectedRoute roles={['owner', 'manager']}><InventoryPage /></ProtectedRoute>} />
                    <Route path="inventario/alertas" element={<ProtectedRoute roles={['owner', 'manager']}><InventoryAlertsPage /></ProtectedRoute>} />
                    <Route path="inventario/plantillas" element={<ProtectedRoute roles={['owner', 'manager']}><InventoryTemplatesPage /></ProtectedRoute>} />
                    <Route path="inventario/importar" element={<ProtectedRoute roles={['owner', 'manager']}><InventoryImportPage /></ProtectedRoute>} />
                    <Route path="configuracion" element={<ProtectedRoute roles={['owner', 'manager']}><BusinessSettingsPage /></ProtectedRoute>} />
                    <Route path="settings" element={<Navigate replace to="/dashboard/configuracion" />} />
                    <Route path="branches" element={<ProtectedRoute roles={['owner', 'manager']}><BranchesPage /></ProtectedRoute>} />
                    <Route path="users" element={<ProtectedRoute roles={['owner', 'manager']}><UsersPage /></ProtectedRoute>} />
                    <Route path="ai-alertas" element={<ProtectedRoute roles={['owner', 'manager', 'reception']}><AiEscalationsPage /></ProtectedRoute>} />
                    <Route path="reception" element={<ProtectedRoute roles={['owner', 'manager', 'reception']}><ReceptionPage /></ProtectedRoute>} />
                    <Route path="kitchen" element={<ProtectedRoute roles={['owner', 'manager', 'kitchen']}><KitchenPage /></ProtectedRoute>} />
                    <Route path="delivery" element={<ProtectedRoute roles={['owner', 'manager', 'delivery']}><DeliveryStaffPage /></ProtectedRoute>} />
                    <Route path="waiter" element={<ProtectedRoute roles={['owner', 'manager', 'waiter']}><WaiterPage /></ProtectedRoute>} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </CartProvider>
            </TenantProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
